// "Updated 2m ago ⟳" — the visible half of the dataset-refresh fix.
//
// The store holds a SNAPSHOT of the CRM, not a live view. That is fine, but
// it has to be legible: before this chip existed the app hydrated once per
// page load and never again, so a record a colleague had just created was
// simply absent with nothing on screen to explain why. Users concluded the
// record had been lost — and re-entered it, which is where the duplicate
// contacts came from (con-jeff-whitten / con-jeff-whitten-2, 39s apart).
//
// See store/refreshDataset.ts for the refresh itself.

import { useEffect, useState } from 'react'
import { RefreshCw } from 'lucide-react'
import { useStore } from '@/store/useStore'
import { refreshDataset } from '@/store/refreshDataset'

/** "just now" / "3m ago" / "1h 20m ago" — no seconds, no ticking clock. */
function ago(ms: number): string {
  const mins = Math.floor(ms / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  return `${hrs}h ${mins % 60}m ago`
}

export function DataFreshnessChip() {
  const lastHydratedAt = useStore((s) => s.lastHydratedAt)
  const pendingWrites = useStore((s) => s.pendingWrites)
  const [now, setNow] = useState(() => Date.now())
  const [busy, setBusy] = useState(false)

  // Re-render once a minute so the label ages without a per-second timer.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(t)
  }, [])

  if (lastHydratedAt === null) return null
  const age = Math.max(0, now - lastHydratedAt)
  // Past this the snapshot is old enough that a stale read is a real risk —
  // say so rather than letting "12m ago" read as reassuring.
  const stale = age > 10 * 60_000

  const click = async () => {
    setBusy(true)
    try {
      await refreshDataset()
      setNow(Date.now())
    } finally {
      setBusy(false)
    }
  }

  return (
    <button
      type="button"
      className={`topbar-fresh${stale ? ' topbar-fresh--stale' : ''}`}
      onClick={() => void click()}
      disabled={busy}
      title={
        pendingWrites > 0
          ? 'Waiting for your last change to save before refreshing'
          : `CRM data as of ${new Date(lastHydratedAt).toLocaleTimeString()} — click to refresh now`
      }
      aria-label="Refresh CRM data"
    >
      <RefreshCw size={13} className={busy ? 'topbar-fresh-spin' : undefined} />
      <span className="topbar-fresh-label">{busy ? 'Refreshing…' : `Updated ${ago(age)}`}</span>
    </button>
  )
}
